import React, { useState } from 'react'
import { useBenefits } from '../BenefitsProvider'
import { Button } from '~/lib/ui/button'
import { cn } from '~/lib/utils'
import MeetingInfo from '../ProfessionalServices/MeetingInfo'
import ResumeRevisions from './ResumeRevisions'
import RevisionDialog from './RevisionDialog'
import useStepInfo from './useStepInfo'
import { useTranslation } from '~/hooks/useTranslation'
import YnsIcon from '~/components/icons/YnsIcon'
import CandidateReviewFeedbackDialog from './CandidateFeedback/CandidateReviewFeedbackDialog'

const Step = ({
  stepKey,
  idx,
  steps,
  scheduleScrollRef,
  resumeReviewScrollRef,
  reviewCompleteScrollRef,
}) => {
  const { t } = useTranslation()
  const { resumeReview, coach, resumeRevisions, serviceRequestList } =
    useBenefits()
  const meetingViaCalendly = resumeReview?.meeting_via_calendly
  const coachRevisions = resumeRevisions?.filter(
    (revision) => revision?.type == 'ResumeRevision::CoachRevision',
  )

  const statusToStepIndex = {
    submitted: 1,
    assigned: meetingViaCalendly ? 2 : 1,
    cancelled: 2,
    scheduled: 2,
    returned: meetingViaCalendly ? 4 : 3,
    revision_requested: meetingViaCalendly ? 4 : 3,
    revision_meeting_requested: meetingViaCalendly ? 4 : 3,
    revision_returned: meetingViaCalendly ? 6 : 5,
    final_revision_returned: meetingViaCalendly ? 6 : 5,
    completed: meetingViaCalendly ? 6 : 5,
    revision_declined: meetingViaCalendly ? 6 : 5,
    closed: meetingViaCalendly ? 6 : 5,
  }
  const stepComplete = () => {
    let index = idx
    if (stepKey === 'scheduleMeeting') {
      index -= 1
    } else if (!meetingViaCalendly && stepKey === 'receiveFeedback') {
      index -= 1
    }
    if (!resumeReview || !resumeReview?.status) return false
    const highestCompletedStep = statusToStepIndex[resumeReview?.status]
    return index <= highestCompletedStep
  }

  const showStepInfo = stepComplete()

  const stepInfo = useStepInfo({
    step: stepKey,
    resumeReview,
    resumeRevisions,
    coach,
    meetingViaCalendly,
    t,
    serviceRequestList,
  })

  const refMap = {
    scheduleMeeting: scheduleScrollRef,
    receiveFeedback: resumeReviewScrollRef,
    finalFeedback: reviewCompleteScrollRef,
  }

  const ref = refMap[stepKey] || null

  return (
    <div ref={ref} key={idx} className="mb-1 flex flex-col gap-1">
      <div className="flex flex-col gap-4 md:flex-row md:items-center">
        <div className="flex flex-shrink-0 items-center gap-4">
          <div
            className={cn(
              'subtitle flex h-8 w-8 items-center justify-center rounded-full',
              idx === 0 || stepComplete(idx)
                ? 'bg-intoo-green-coachcta text-white'
                : 'bg-willow-brook text-intoo-green-coachcta',
            )}
          >
            {idx + 1}
          </div>
          <div className="subtitle text-intoo-green-coachcta">
            {stepKey == 'finalFeedback' && resumeReview?.revision_declined
              ? t(`steps.${stepKey}.title_no_revisions`, {
                  date: new Date(
                    resumeReview?.revision_declined_at,
                  ).toLocaleDateString(),
                })
              : stepKey == 'finalFeedback' && resumeReview?.closed
                ? t(`steps.${stepKey}.title_closed`, {
                    date: new Date(
                      resumeReview?.completed_at,
                    ).toLocaleDateString(),
                  })
                : t(`steps.${stepKey}.title`)}
          </div>
        </div>

        {showStepInfo && (
          <StepInfo
            step={stepKey}
            stepInfo={stepInfo}
            resumeReview={resumeReview}
          />
        )}
      </div>
      <div className="relative">
        {idx !== steps.length - 1 && (
          <div className="absolute left-3 h-full w-1 bg-willow-brook pb-1"></div>
        )}
        <div className="ml-12 pb-4">
          {t(
            `steps.${stepKey}.${
              stepKey === 'finalFeedback' && resumeReview?.closed
                ? 'description_closed'
                : 'description'
            }`,
          )}
          {stepKey === 'scheduleMeeting' &&
            (resumeReview?.status === 'assigned' ||
              resumeReview?.status === 'scheduled' ||
              resumeReview?.status === 'cancelled') && (
              <div className="mt-4">
                <MeetingInfo
                  calendlyEvent={
                    serviceRequestList['ServiceRequest::ResumeReview']
                      ?.calendly_event
                  }
                  requestType="ServiceRequest::ResumeReview"
                  buttonText={`Schedule Meeting with Coach ${coach.first_name} `}
                  requestComplete={resumeReview?.status == 'returned'}
                  eventName="Meeting"
                  cardTitle="Schedule Your Coach Meeting"
                  meetingViaCalendly={meetingViaCalendly}
                  flexResumeReview={true}
                />
              </div>
            )}
          {stepKey === 'receiveFeedback' && resumeRevisions?.length > 0 && (
            <RevisionFeedback revision={resumeRevisions[0]} />
          )}
          {stepKey === 'askForRevisions' && resumeRevisions?.length > 0 && (
            <ResumeRevisions />
          )}
          {stepKey === 'finalFeedback' &&
            (resumeReview?.closed ||
              resumeReview?.status === 'final_revision_returned' ||
              resumeReview?.revision_declined) &&
            !resumeReview?.feedbacked && (
              <div className="mt-4">
                <CandidateReviewFeedbackDialog
                  choices={['Poor', 'Fair', 'Good', 'Very Good', 'Excellent']}
                  admonition="Have any feedback for us? We'd love to hear it!"
                  serviceRequestType="resume_review"
                  serviceRequestName="Resume Review"
                />
              </div>
            )}
          {stepKey === 'finalFeedback' &&
            resumeReview?.closed &&
            !resumeReview?.revision_declined && (
              <div className="mt-4">
                <RevisionFeedback
                  revision={coachRevisions[coachRevisions.length - 1]}
                />
              </div>
            )}
        </div>
      </div>
    </div>
  )
}

const StepInfo = ({ step, stepInfo, resumeReview }) => {
  return (
    <>
      {stepInfo && (
        <div className="ml-2 bg-willow-brook p-2 font-medium text-intoo-green-coachcta">
          {stepInfo}
        </div>
      )}
      {step == 'submitResume' && <SubmitResume resumeReview={resumeReview} />}
    </>
  )
}

const SubmitResume = ({ resumeReview }) => {
  const { t } = useTranslation('steps')
  const { onScheduleClick } = useBenefits()
  const handleUploadNewResumeClick = () => {
    onScheduleClick(
      'UploadNewResume',
      t('submitResume.upload_new_resume_modal_title'),
      resumeReview?.meeting_via_calendly,
    )
  }
  return (
    <div className="flex items-center gap-2">
      <a
        href={resumeReview?.resume_url}
        target="_blank"
        rel="noopener noreferrer"
        className="ml-2 flex items-center gap-2 hover:!underline"
      >
        {resumeReview?.resume_name}
        <YnsIcon name="download" className="h-6 w-6" />
      </a>
      {resumeReview && (
        <Button
          onClick={handleUploadNewResumeClick}
          variant="link"
          title={t('submitResume.upload_new_resume')}
        >
          <YnsIcon name="refresh" className="ml-2" />
        </Button>
      )}
    </div>
  )
}

const RevisionFeedback = ({ revision }) => {
  const [showRevisionDialog, setShowRevisionDialog] = useState(false)
  const toggleShowRevisionDialog = () => {
    setShowRevisionDialog(!showRevisionDialog)
  }
  const { t } = useTranslation()
  return (
    <div className="mt-4 flex items-center gap-4">
      <Button variant="coachcta" onClick={() => setShowRevisionDialog(true)}>
        {t('view_feedback')}
      </Button>
      <RevisionDialog
        revision={revision}
        showRevisionDialog={showRevisionDialog}
        toggleShowRevisionDialog={toggleShowRevisionDialog}
      />
      <div>
        <a
          href={revision?.remote_content_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 hover:!underline"
        >
          {revision?.remote_content}
          <YnsIcon name="download" className="h-6 w-6" />
        </a>
      </div>
    </div>
  )
}

export { Step, RevisionFeedback }
